import { useCallback, useState } from "react";
import { Button, Flex, Spinner, Text } from "@radix-ui/themes";
import { PlusIcon } from "@radix-ui/react-icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FilterMenuItemType, FilterMenuType } from "./FilterMenu.model";
import { FilterMenuItem } from "./filter-menu-item/FilterMenuItem";
import { FieldFilter } from "./FieldFilter.model";
import { ActiveFilters } from "./ActiveFilters";

interface FilterMenuProps<Test extends Record<string, FieldFilter>> {
  size?: "1" | "2" | "3";
  label?: string;
  icon?: React.ReactNode;
  loading?: boolean;
  count?: number;
  viewOnly?: boolean;
  hideDropdownButton?: boolean;
  hideClearButton?: boolean;
  menu: FilterMenuType<Extract<keyof Test, string>>;
  value: Partial<Test>;
  hideCount?: boolean;
  onChange: (value: Partial<Test>) => void;
  onClearAll?: () => void;
}

export function FilterMenu<Test extends Record<string, FieldFilter>>({
  size = "1",
  label = "Filter",
  icon = <PlusIcon />,
  loading = false,
  count,
  viewOnly = false,
  hideDropdownButton = false,
  hideClearButton = false,
  menu,
  value,
  hideCount = false,
  onChange,
  onClearAll,
}: FilterMenuProps<Test>) {
  const [open, setOpen] = useState(false);

  const handleChange = useCallback(
    (item: FilterMenuItemType<Extract<keyof Test, string>>) => {
      if (item.value == null) {
        const newValue = { ...value };
        delete newValue[item.field];
        onChange(newValue);
        return;
      }

      const newValue = { ...value, [item.field]: item.value };
      onChange(newValue);
    },
    [value, onChange]
  );

  const handleRequestClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleClearAll = useCallback(() => {
    onChange({});
  }, [onChange]);

  return (
    <Flex align="start" gap="2" wrap="wrap">
      {!viewOnly && !hideDropdownButton && (
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger>
            <Button color="gray" variant="surface" highContrast size={size}>
              {icon}
              {label}
              {loading && <Spinner size="1" />}
              {!loading && count != null && !hideCount && (
                <Text color="gray" className="ml-1">
                  {count.toLocaleString("en-US")}
                </Text>
              )}
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="start">
            {menu.map((item) => (
              <FilterMenuItem
                key={item.field}
                item={item}
                value={value[item.field]}
                onChange={handleChange}
                onRequestClose={handleRequestClose}
              />
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <ActiveFilters
        size={size}
        value={value}
        menu={menu}
        viewOnly={viewOnly}
        onChange={handleChange}
      />

      {Object.entries(value).length > 0 && !viewOnly && !hideClearButton && (
        <Button
          variant="soft"
          color="gray"
          size={size}
          onClick={onClearAll ? onClearAll : handleClearAll}
        >
          Clear
        </Button>
      )}
    </Flex>
  );
}
